/**
 * Public checks — the PAYER's client service for pay at table (Guest QR
 * Platform, piece 3, Task 8).
 *
 * Mirrors `apps/api/components/routes/public/check-pay.ts`, one async fn per
 * route, and sits beside `publicTables.ts` for the same reason it does: there
 * is NO auth here. A guest scanning a table QR (or the QR on a printed
 * pre-bill) arrives with a signed check access token and nothing else — no
 * Firebase session, no App Check token — so every call goes through the
 * Firebase-free public client (`./publicApiClient`), and `CheckPayPage` mounts
 * OUTSIDE `<CustomerSession>` (Ruling R57) for the same reason.
 *
 *   GET  /public/checks/:checkToken               → `getGuestCheck`
 *   POST /public/checks/:checkToken/card-session  → `startCheckCardSession`
 *   POST /public/checks/:checkToken/pay           → `payGuestCheck`
 *
 * ── The token is the WHOLE request ──────────────────────────────────────────
 * `checkGuid`, `tenantId` and `restaurantId` are derived from the token
 * server-side and never accepted from a client (spec §4.1). So nothing here
 * takes an id, the pay body is `.strict()` on the server, and a body carrying
 * one is a 400 rather than a field the handler has to remember not to read.
 * That is why {@link payGuestCheck} posts its body verbatim and builds
 * nothing: anything this file "helpfully" added would be refused.
 *
 * ── Misses are a STATUS CODE, not a body ────────────────────────────────────
 * Every miss — malformed / unsigned / tampered / expired token, a check the
 * verified scope has none of, a voided or superseded check, a restaurant with
 * pay-at-table switched off — is ONE byte-identical 404 `{"state":"unknown"}`,
 * deliberately not an envelope, so the route cannot be used as an existence
 * oracle. There is therefore nothing in the body to branch on:
 * {@link isCheckMiss} reads the status the public client stamps on the thrown
 * error, exactly as `isTableMiss` does.
 *
 * ── Refusals that are ANSWERS, not failures ─────────────────────────────────
 * {@link startCheckCardSession} resolves rather than throws for a refusal of
 * the card session itself (`400 MERCHANT_NOT_CONFIGURED`, any other 4xx such
 * as a session refused for a non-TRX route, a `502` from TRX's own
 * Initialize): that is a real "no card form here", but it is the CARD FORM's
 * state, not the page's — the bill, the tender summaries and the balance all
 * still render.
 *
 * A 404 still THROWS from all three calls — that one is the page's state (the
 * link is dead), not any one component's.
 */

import type { GuestCheckView } from '@order-up/common';

import {
  createPublicApiClient,
  unwrapPublicEnvelope,
  type PublicApiError,
} from './publicApiClient';

const publicClient = createPublicApiClient();

// ─── Wire shapes ─────────────────────────────────────────────────────────────

/**
 * The PayPage session triple a TRX-routed business's card fields mount
 * against — the exact shape `TrxCardFields`' `session` prop takes. The route
 * also answers an `expirationTime`; it is deliberately not carried here,
 * because nothing in the page reads it and the session outlives a payer's
 * visit in practice.
 */
export interface CheckCardSession {
  readonly clientId: string;
  readonly payPageId: string;
  readonly sessionId: string;
}

/**
 * What {@link startCheckCardSession} could establish about the card form. See
 * the module header: `unavailable` is the route REFUSING, and is the answer
 * the page needed rather than a failure to report.
 */
export type CheckCardSessionOutcome =
  | {
      kind: 'trx';
      session: CheckCardSession;
      /**
       * `/pay` will AUTHORIZE this card now and the restaurant's end-of-day
       * pass will capture it (`initiative.online_auth_capture`). The page tells
       * the guest before they pay. Absent from an older API → false.
       */
      cardHold: boolean;
    }
  | { kind: 'unavailable'; code: string; message: string };

/** The card instrument the pay body accepts — provider-discriminated, already tokenized. */
export type CheckPaymentInstrument = {
  readonly provider: 'trx';
  readonly encrypted: { readonly data: string; readonly keyId: string };
};

/**
 * An Apple Pay / Google Pay payload, the OTHER thing `/pay` accepts.
 *
 * It carries NO `provider`, and that is not an omission. A card instrument is
 * provider-discriminated because a tokenized card is an object only the gateway
 * that minted it can read; a wallet payload is
 * produced by whichever sheet the page mounted for the gateway the SERVER
 * already routed to, so there is nothing for the guest to assert and nothing
 * for the route to cross-check. Mirrors `CheckoutWalletPaymentSchema`
 * (`@order-up/schemas`) field for field — the same shape `/checkout` takes, so
 * one wallet result serves both surfaces untranslated.
 *
 * `cardSecurity` is the Visa CAVV / Mastercard DSRP cryptogram and expires in
 * about a minute: the page submits on the sheet's callback, never behind
 * another confirmation step.
 */
export interface CheckWalletPayment {
  readonly walletName: 'ApplePay' | 'GooglePay';
  readonly data: string;
  /** TRX JS-library path only; absent for the owned Apple/Google Pay sheet. */
  readonly keyId?: string;
  readonly bin?: string;
  readonly cardSecurity?: { readonly cavv?: string; readonly dsrpCryptogram?: string };
}

/**
 * `POST /pay`'s body. Mirrors the server's strict Zod schema field for field —
 * each mode's own field is REQUIRED and the other modes' fields are REFUSED,
 * so this type is the loose superset and the server is the authority.
 */
export interface GuestCheckPayBody {
  readonly mode: 'full' | 'items' | 'even' | 'amount';
  readonly itemGuids?: readonly string[];
  readonly evenParts?: number;
  readonly evenShare?: number;
  readonly amountCents?: number;
  readonly tipCents: number;
  /**
   * The payer's billing postal (Ruling R63). Optional on the wire and
   * ALWAYS sent by `CheckPayPage`: without it the sale carries no `avsresp`,
   * and the fraud rule reads an absent AVS result as ALLOW — it fails open, so
   * a restaurant that set `enforcement: 'reject'` would get no AVS protection
   * at all on this surface and nothing would report it.
   */
  readonly billingZip?: string;
  /**
   * EXACTLY ONE of `payment` / `walletPayment`, enforced server-side. Both
   * optional here because this type is the loose superset (see above) and the
   * strict Zod schema is the authority — but a body carrying neither, or both,
   * is a 400, not a body the handler picks an arm from.
   */
  readonly payment?: CheckPaymentInstrument;
  /** Apple Pay / Google Pay, in place of `payment`. */
  readonly walletPayment?: CheckWalletPayment;
  /** Receipt-only. Never authorization, never identity. */
  readonly contact?: {
    readonly firstName?: string;
    readonly lastName?: string;
    readonly email?: string;
    readonly phone?: string;
  };
}

/** What an APPROVED charge answers with. */
export interface GuestCheckPayResult {
  /** The `PaymentRecord` id. `null` on a replay — the lock records no key. */
  readonly paymentGuid: string | null;
  readonly processorReference: string;
  /** The portion applied to the CHECK. `null` on a replay. */
  readonly baseCents: number | null;
  readonly tipCents: number | null;
  /** The processor's ECHOED captured total (base + tip). */
  readonly chargedCents: number;
  readonly cardLast4: string | null;
  /** `true` when this answers a PRIOR charge rather than a new one. */
  readonly replayed: boolean;
}

// ─── Calls ───────────────────────────────────────────────────────────────────

/**
 * The guest-safe projection of the ONE check the token names. Throws on every
 * non-2xx, including the uniform miss — callers identify that with
 * {@link isCheckMiss} rather than by reading a body that says the same thing
 * for every miss.
 *
 * A fully-paid check still answers 200 (`fullyPaid: true`); that is the payer's
 * confirmation, not a miss.
 */
const getGuestCheck = (checkToken: string): Promise<GuestCheckView> =>
  unwrapPublicEnvelope<{ check: GuestCheckView }>(
    publicClient.get(`/checks/${encodeURIComponent(checkToken)}`),
  ).then((data) => data.check);

/**
 * Establish how this business's card form is mounted. See the module header
 * for why two refusals resolve instead of throwing.
 */
async function startCheckCardSession(checkToken: string): Promise<CheckCardSessionOutcome> {
  try {
    const data = await unwrapPublicEnvelope<CheckCardSession & { cardHold?: boolean }>(
      publicClient.post(`/checks/${encodeURIComponent(checkToken)}/card-session`),
    );
    return {
      kind: 'trx',
      session: {
        clientId: data.clientId,
        payPageId: data.payPageId,
        sessionId: data.sessionId,
      },
      cardHold: data.cardHold === true,
    };
  } catch (error) {
    const typed = error as PublicApiError | undefined;
    // A dead link is the PAGE's state — let it through so the page, not the
    // card form, answers for it.
    if (isCheckMiss(error)) throw error;
    if (typed?.status !== undefined && typed.status < 500) {
      return {
        kind: 'unavailable',
        code: typed.code ?? 'CARD_SESSION_UNAVAILABLE',
        message: typed.message,
      };
    }
    if (typed?.code === 'CARD_SESSION_UNAVAILABLE') {
      return { kind: 'unavailable', code: typed.code, message: typed.message };
    }
    throw error;
  }
}

/**
 * THE CHARGE. The body goes out verbatim (see the module header) and every
 * refusal is thrown with the server's own `code` and `message` stamped on it,
 * because the page renders them DIFFERENTLY: `ITEMS_ALREADY_CLAIMED` is
 * another guest having covered this selection — not an error — while
 * `CARD_REJECTED` means no other card will help and `PAYMENT_IN_PROGRESS`
 * means try again in a moment.
 */
const payGuestCheck = (
  checkToken: string,
  body: GuestCheckPayBody,
): Promise<GuestCheckPayResult> =>
  unwrapPublicEnvelope<GuestCheckPayResult>(
    publicClient.post(`/checks/${encodeURIComponent(checkToken)}/pay`, body),
  );

/**
 * True when the error is the routes' uniform miss — the token is malformed,
 * unsigned, tampered with or expired; the check is voided, superseded or not
 * in the token's own scope; or the restaurant has pay-at-table off.
 *
 * 414 counts: find-my-way answers it before any handler for a param past the
 * router's ceiling, so it is a miss with a different number on it. Anything
 * else — 5xx, a network failure, the deployment-protection wall — is "we do not
 * know", which must never be shown to a guest as "no such check".
 */
export function isCheckMiss(error: unknown): boolean {
  const status = (error as PublicApiError | undefined)?.status;
  return status === 404 || status === 414;
}

export const publicChecksApi = {
  getGuestCheck,
  startCheckCardSession,
  payGuestCheck,
};

export { getGuestCheck, startCheckCardSession, payGuestCheck };
